import React, { useEffect, useState } from 'react';
import { Grid, styled } from '@mui/material';
import useTrackingCookies from 'parent-portal/shared/EngagementSourceTracking';
import usePublicSessionTracking from 'parent-portal/shared/PublicSessionTracking';
import { client, consoleLogInDev } from 'shared';
import { Chip, Colors, Text, WebLink } from 'library';
import useQueryParams from 'shared/QueryParamsHook';
import States from '../../../../constants/States';

interface IIndeedJob {
    id: number;
    city: string;
    state: string;
    zip_code: string;
    title: string;
    alt_title: string;
    pay_string: string;
}

export default function Workers() {
    useTrackingCookies('/workers');
    const { usePageLoadTracking } = usePublicSessionTracking('workers');
    usePageLoadTracking();
    const query = useQueryParams();
    const [jobs, setJobs] = useState<IIndeedJob[]>([]);
    const selectedState = query.get('state');

    const stateAbbrs = Object.keys(States);
    const stateValues = Object.values(States).map((x) => x.toLowerCase());

    useEffect(() => {
        const qs = selectedState ? `?state=${selectedState}` : '';
        client(`api/indeed-jobs/list-jobs/${qs}`)
            .then((response) => {
                setJobs(response);
            })
            .catch((error) => {
                consoleLogInDev(error);
            });
    }, []);

    const notSelectedStyle = {
        color: Colors.darkNavy,
        backgroundColor: Colors.white,
        borderColor: Colors.darkNavy,
        borderWidth: 1,
        borderStyle: 'solid',
    };

    function getStateAbbr(state: string) {
        const normalState = state.replaceAll('_', ' ').toLocaleLowerCase();
        const valueIndex = stateValues.indexOf(normalState);
        return stateAbbrs[valueIndex];
    }

    return (
        <Container>
            <Text variant="h1">Available Jobs</Text>
            <LinkContainer container direction="row">
                <PrimaryLink href="listings" style={selectedState ? notSelectedStyle : {}}>
                    All
                </PrimaryLink>
                <PrimaryLink href="listings?state=Ohio" style={selectedState !== 'Ohio' ? notSelectedStyle : {}}>
                    Ohio
                </PrimaryLink>
                <PrimaryLink href="listings?state=Florida" style={selectedState !== 'Florida' ? notSelectedStyle : {}}>
                    Florida
                </PrimaryLink>
                <PrimaryLink
                    href="listings?state=Illinois"
                    style={selectedState !== 'Illinois' ? notSelectedStyle : {}}
                >
                    Illinois
                </PrimaryLink>
                <PrimaryLink
                    href="listings?state=North_Carolina"
                    style={selectedState !== 'North_Carolina' ? notSelectedStyle : {}}
                >
                    North Carolina
                </PrimaryLink>
            </LinkContainer>
            <Grid container spacing={2}>
                {jobs.map((x) => (
                    <JobListingCard key={x.id} job={x} getStateAbbr={getStateAbbr} />
                ))}
                {!jobs.length ? (
                    <Container>
                        <Text>No job listings found</Text>
                    </Container>
                ) : null}
            </Grid>
        </Container>
    );
}

function JobListingCard({ job, getStateAbbr }: { job: IIndeedJob; getStateAbbr: (state: string) => string }) {
    return (
        <Grid item xs={12} sm={6} md={4}>
            <JobCard>
                <JobLink href={`/job-listings/${job.id}`}>{job.alt_title}</JobLink>
                <ChipContainer>
                    <Chip
                        backgroundColor={Colors.turquoise}
                        textColor={Colors.white}
                        label="Full-Time"
                        style={{ maxHeight: 25, minHeight: 25, marginRight: 5 }}
                    />
                    <Chip
                        backgroundColor={Colors.terraCotta}
                        textColor={Colors.white}
                        label="Part-Time"
                        style={{ maxHeight: 25, minHeight: 25 }}
                    />
                </ChipContainer>
                <Grid item>
                    <Text>
                        {job.city}, {getStateAbbr(job.state)}
                    </Text>
                </Grid>
                <Grid container item direction="row" justifyContent="space-between" alignItems="center">
                    <Text variant="caption" bold textStyle={{ display: 'inline-block' }}>
                        {job.pay_string}
                    </Text>
                    <PrimaryLink href={`/application/apply/${job.id}`}>Apply Now</PrimaryLink>
                </Grid>
            </JobCard>
        </Grid>
    );
}

const Container = styled('div')({
    padding: 20,
    width: '100%',
});

const ChipContainer = styled('div')({
    padding: '5px 0',
});

const JobCard = styled(Grid)({
    height: '100%',
    borderRadius: 20,
    boxShadow: '0px 3px 6px #00000029',
    padding: 10,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-evenly',
});

const JobLink = styled('a')({
    color: Colors.darkNavy,
    fontWeight: 'bold',
    fontSize: 15,
    textDecoration: 'underline',
    fontFamily: 'Outfit',
});

const LinkContainer = styled(Grid)({
    margin: '10px 0',
});

const PrimaryLink = styled('a')({
    borderRadius: 20,
    padding: '5px 8px',
    fontSize: 13,
    backgroundColor: Colors.darkNavy,
    color: Colors.white,
    margin: 2,
    minWidth: 60,
    textAlign: 'center',
    boxShadow: '0px 3px 6px #807E7D80',
    fontFamily: 'Outfit',

    '&:hover': {
        color: Colors.white,
    },
});
